import * as React from 'react';
import {TypeImplementation} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {TText, EText} from '@packages/systems/wfdl-ast';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {
  SegmentedControl,
  SegmentedControlButton,
} from '@packages/systems/spring';

type Value = {
  value: string;
  label: React.ReactElement<any> | string;
  tooltip?: string;
  disabled?: boolean;
};

type Props = {
  items: Array<Value>;
};

type ComponentProps = {
  items: Array<Value>;
  handleClick: (value: string) => void;
  selectedItem: Value;
};

export const Component = ({
  items,
  handleClick,
  selectedItem,
}: ComponentProps) => (
  <SegmentedControl onChange={handleClick} value={selectedItem.value}>
    {items.map((item) => (
      <SegmentedControlButton
        key={item.value}
        isDisabled={item.disabled}
        data-automation-id={item.value}
        value={item.value}
      >
        <TooltipTrigger content={item.tooltip} alternate>
          <div>{item.label}</div>
        </TooltipTrigger>
      </SegmentedControlButton>
    ))}
  </SegmentedControl>
);

export const renderEnumWithTooltip =
  ({items}: Props) =>
  ({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: TypeImplementation.ExpressionEditorDispatch;
  }) => {
    const strippedExpr = stripAnn(expr);
    const text = ContextualExpressions.getText(strippedExpr);

    const {type} = getAnn(expr);
    const getItem = (textValue: null | string) =>
      items.find((item) => item.value === textValue) || items[0];
    const [selectedItem, setSelectedItem] = React.useState(getItem(text));

    if (type === null) {
      throw Error('Expected type from AnnotatedExpression to be defined');
    }

    React.useEffect(() => setSelectedItem(getItem(text)), [text]);

    const handleClick = (textValue: string) => {
      const item = getItem(textValue);
      if (item) {
        setSelectedItem(item);
        dispatch(updateExpressionAction(EText(item.value)));
      }
    };

    return (
      <Component
        items={items}
        handleClick={handleClick}
        // @ts-expect-error - TS2322 - Type 'Value | undefined' is not assignable to type 'Value'.
        selectedItem={selectedItem}
      />
    );
  };

export const enumWithTooltip = (items: Array<Value>) =>
  TypeImplementation.Alias({
    type: TText,
    render: renderEnumWithTooltip({
      items,
    }),
  });
